import { FunctionComponent, useEffect, useRef, useState } from "react";

import Link from "components/common/ui/Link/Link";
import LoadingSpinner from "components/common/ui/LoadingSpinner/LoadingSpinner";
import Page from "components/common/ui/Page/Page";
import { PageType } from "ts/enums";
import PathTranslationKey from "utilities/paths";
import { dark } from "utilities/variables/colors";
import { EmbeddedCheckout, embedCheckout } from "@bigcommerce/checkout-sdk";
import { isIOS } from "react-device-detect";
import { remCalc } from "utilities/styles";
import styled from "styled-components/macro";
import useCart from "hooks/cart/useCart";
import { useLazyGetCartQuery } from "app/api/cart/cartApi";
import useLocaleNavigate from "hooks/reactRouterWrappers/useLocaleNavigate";
import { useSelector } from "redux/hooks";
import { useTranslation } from "react-i18next";

const Title = styled.h2`
	color: ${dark};
	font-size: ${remCalc(20)};
	line-height: ${remCalc(24)};
	font-weight: bold;
	text-align: center;
	margin: 0;
	margin-top: ${remCalc(16)};
	padding: 0 16px;
`;

const SubTitle = styled.h4`
	color: ${dark};
	font-size: ${remCalc(14)};
	line-height: ${remCalc(17)};
	font-weight: normal;
	text-align: center;
	margin: 0;
	margin-bottom: ${remCalc(16)};
	padding: 0 16px;

	a {
		text-decoration: underline;
	}
`;

const Checkout: FunctionComponent = () => {
	const { cart, cartExists, deleteCart } = useCart();
	const { t } = useTranslation();
	const navigate = useLocaleNavigate();

	const [getCart, getCartResult] = useLazyGetCartQuery();

	const [embeddedLoaded, setEmbeddedLoaded] = useState(false);
	const embeddedCheckoutRef = useRef<EmbeddedCheckout | null>(null);
	const embedRequestIdRef = useRef(0);

	const store = useSelector((state) => state.bcStore.store);

	const isNetherlands = store?.code === "nl";

	// We don't embed the checkout for iOS users because of a capacitor domain limitation on iOS.
	// Instead, we redirect them to the checkout page.
	// IIRC it's to do with the URL on iOS being capacitor://www.monaonline.com instead of https://www.monaonline.com,
	// but this should be double checked and documented here.

	// We don't embed the checkout for Netherlands users because the payment method iDEAL's security policies prevent it
	// from being rendered within an iframe, and creating a mechanism to redirect the top level window instead is more
	// complicated and given the short time we have to complete this ticket, we've opted to go with this faster approach.
	// Moreover, the payment method does not provide us a convenient mechanism to trigger the redirection, so it's bound to be
	// more complicated than it needs to be.
	const shouldRedirectToCheckout = isIOS || isNetherlands;

	const embedCurrentCart = () => {
		if (!cart) {
			return;
		}

		// embedCurrentCart can fire more than once in quick succession right
		// after login (cart updates from useCartOnLogin and useCart's own
		// customer-assignment effect can both land before the first embed
		// settles). Only the most recent call's result should ever reach the
		// DOM — otherwise embedCheckout() calls stack up and leave multiple
		// iframes in #checkout-app, since detach() on the ref can't run until
		// the prior (still-pending) embed actually resolves.
		const requestId = ++embedRequestIdRef.current;

		getCart(cart.id)
			.unwrap()
			.then((res) => {
				if (requestId !== embedRequestIdRef.current) {
					return;
				}

				if (!store) {
					console.error(
						"No store available. This is not expected at this time",
					);
					return;
				}

				let url = res.redirect_urls.embedded_checkout_url;

				if (res.embed_checkout_jwt) {
					url = `https://${
						new URL(res.redirect_urls.checkout_url).hostname
					}/login/token/${res.embed_checkout_jwt}`;
				}

				if (shouldRedirectToCheckout) {
					return (window.location.href = url);
				}

				if (url) {
					// Detach the previous embed if we have a handle to it, and
					// also clear the container synchronously — detach() alone
					// isn't enough here since embedCheckout() is async and the
					// ref may not be populated yet if calls overlap.
					embeddedCheckoutRef.current?.detach();
					embeddedCheckoutRef.current = null;

					const container = document.getElementById("checkout-app");

					if (container) {
						container.innerHTML = "";
					}

					embedCheckout({
						containerId: "checkout-app",
						url: String(url),
						onSignOut: () => navigate(t(PathTranslationKey.SIGN_OUT)),
						onComplete: () => deleteCart(),
						onLoad: () => setEmbeddedLoaded(true),
					}).then((instance) => {
						if (requestId !== embedRequestIdRef.current) {
							instance.detach();

							return;
						}

						embeddedCheckoutRef.current = instance;
					});
				}
			});
	};

	useEffect(() => {
		if (cart) {
			embedCurrentCart();

			return;
		}
		if (!embeddedLoaded && !cartExists()) {
			navigate(t(PathTranslationKey.CART));
		}
	}, [cart]);

	// BigCommerce can merge the guest cart into the customer's own saved cart
	// when they log in from within the embedded checkout. That invalidates the
	// checkoutId baked into the currently embedded iframe's URL, so the
	// checkout tells us via postMessage to fetch a fresh embedded-checkout URL
	// for the cart and re-embed it — the same recovery that happens when a
	// shopper leaves and re-enters checkout manually.
	useEffect(() => {
		const checkoutOriginPattern = process.env.REACT_APP_CHECKOUT_ORIGIN_PATTERN;

		if (!checkoutOriginPattern) {
			return;
		}

		const onCartMergedMessage = (event: MessageEvent) => {
			if (event.data?.type !== "CHECKOUT_CART_MERGED") {
				return;
			}

			// eslint-disable-next-line no-console
			console.log("[DEBUG] CHECKOUT_CART_MERGED received", {
				origin: event.origin,
				pattern: checkoutOriginPattern,
				matches: Boolean(event.origin.match(checkoutOriginPattern)),
			});

			if (event.origin.match(checkoutOriginPattern)) {
				embedCurrentCart();
			}
		};

		window.addEventListener("message", onCartMergedMessage);

		return () => {
			window.removeEventListener("message", onCartMergedMessage);
		};
	}, [cart]);

	return (
		<Page pageType={PageType.DEFAULT}>
			<div id="checkout-app"></div>
			{getCartResult.isLoading || getCartResult.isFetching ? (
				<LoadingSpinner />
			) : shouldRedirectToCheckout ? (
				<>
					<Title>{t("checkout.title")}</Title>
					<SubTitle>
						<Link to={`${String(t(PathTranslationKey.CART))}`}>
							{t("checkout.subtitle")}
						</Link>
					</SubTitle>
				</>
			) : null}
		</Page>
	);
};

export default Checkout;
